import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type Profile = Database['public']['Tables']['profiles']['Row'];
type ProfileInsert = Database['public']['Tables']['profiles']['Insert'];
type ProfileUpdate = Database['public']['Tables']['profiles']['Update'];

export function useProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
}

export function useCreateProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (profile: Omit<ProfileInsert, 'user_id'>) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('profiles')
        .insert({ ...profile, user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (profile: ProfileUpdate) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('profiles')
        .update(profile)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useProfileById(userId: string | undefined) {
  return useQuery({
    queryKey: ['profile', userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function useProfiles(filters?: { cityCohort?: string; isMentor?: boolean; search?: string }) {
  return useQuery({
    queryKey: ['profiles', filters],
    queryFn: async () => {
      let query = supabase.from('profiles').select('*');

      // Only show approved members in the directory
      query = query.eq('membership_status', 'approved');

      if (filters?.cityCohort) {
        query = query.eq('city_cohort', filters.cityCohort);
      }

      if (filters?.isMentor !== undefined) {
        query = query.eq('is_mentor', filters.isMentor);
      }

      if (filters?.search) {
        query = query.or(`full_name.ilike.%${filters.search}%,company.ilike.%${filters.search}%,role.ilike.%${filters.search}%`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 2,
  });
}

export function useProfilesPaged(params: {
  page: number;
  pageSize: number;
  cityCohort?: string;
  cityCohorts?: string[];
  isMentor?: boolean;
  search?: string;
  industry?: string;
}) {
  return useQuery({
    queryKey: ['profiles', 'paged', params],
    queryFn: async () => {
      const from = params.page * params.pageSize;
      const to = from + params.pageSize - 1;

      let query = supabase
        .from('profiles')
        .select('*', { count: 'exact' })
        .eq('membership_status', 'approved');

      if (params.cityCohorts && params.cityCohorts.length > 0) {
        query = query.in('city_cohort', params.cityCohorts);
      } else if (params.cityCohort) {
        query = query.eq('city_cohort', params.cityCohort);
      }

      if (params.isMentor !== undefined) {
        query = query.eq('is_mentor', params.isMentor);
      }

      if (params.industry) {
        query = query.eq('industry', params.industry);
      }

      if (params.search) {
        query = query.or(
          `full_name.ilike.%${params.search}%,company.ilike.%${params.search}%,role.ilike.%${params.search}%`
        );
      }

      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;
      return {
        data: data || [],
        count: count || 0,
      };
    },
    staleTime: 1000 * 60 * 2,
  });
}
